/**
 * Bloque 2 — Catálogo de métricas.
 *
 * Es el corazón del diseño: un KPI es un registro, así que esta tabla **es** el
 * lugar donde se agrega un KPI. La severidad de la fila mide deuda del catálogo,
 * no del negocio: una métrica sin ningún umbral no puede tener semáforo en
 * ninguna pantalla, y una sin dueño es una que nadie va a cargar.
 */
import {
  SOURCE_LABELS,
  formatMetricValue,
  type Health,
  type MetricDefinition,
  type User,
} from '@ops/contracts'

import { Button, DataTable, Icon, Section, type Column } from '../../ui'
import { NotaFuente, SinDato } from './Estados'
import {
  DIRECCION_LABELS,
  DIRECCION_OPERADOR,
  UNIDAD_LABELS,
} from './etiquetas'

export interface CatalogoPanelProps {
  metricas: readonly MetricDefinition[]
  usuarios: readonly User[]
  onEditar: (metrica: MetricDefinition) => void
  onAgregar: () => void
  id?: string
}

/** Deuda del catálogo, no del negocio: sin umbral no hay semáforo posible. */
export function severidadDeMetrica(metrica: MetricDefinition): Health {
  if (metrica.umbralOk === null && metrica.umbralWarning === null) {
    return 'critical'
  }
  if (metrica.ownerId === null) return 'warning'
  return 'ok'
}

export function CatalogoPanel({
  metricas,
  usuarios,
  onEditar,
  onAgregar,
  id,
}: CatalogoPanelProps) {
  const nombrePorId = new Map(usuarios.map((u) => [u.id, u.nombre]))
  const sinSemaforo = metricas.filter(
    (m) => severidadDeMetrica(m) === 'critical',
  ).length
  const sinDueno = metricas.filter((m) => m.ownerId === null).length
  const sinFormula = metricas.filter((m) => m.formula === null).length

  const columns: ReadonlyArray<Column<MetricDefinition>> = [
    {
      key: 'metrica',
      header: 'Métrica',
      rowHeader: true,
      width: '210px',
      sortValue: (m) => m.nombre,
      filterValue: (m) => `${m.nombre} ${m.slug} ${m.definicion}`,
      render: (m) => (
        <span className="flex min-w-0 flex-col">
          <span className="truncate">{m.nombre}</span>
          <code className="truncate text-micro font-normal text-muted-strong">
            {m.slug}
          </code>
        </span>
      ),
    },
    {
      key: 'definicion',
      header: 'Definición',
      width: '280px',
      sortValue: (m) => m.definicion,
      render: (m) => (
        <span className="line-clamp-2 max-w-[280px]" title={m.definicion}>
          {m.definicion}
        </span>
      ),
    },
    {
      key: 'unidad',
      header: 'Unidad',
      width: '110px',
      sortValue: (m) => UNIDAD_LABELS[m.unidad],
      render: (m) => UNIDAD_LABELS[m.unidad],
    },
    {
      key: 'umbrales',
      header: 'Umbrales del semáforo',
      width: '210px',
      sortValue: (m) => m.umbralOk ?? m.umbralWarning,
      render: (m) => {
        const op = DIRECCION_OPERADOR[m.direccion]
        if (m.umbralOk === null && m.umbralWarning === null) {
          return (
            <SinDato
              que="umbral"
              onCargar={() => onEditar(m)}
              textoAccion="Definilos"
            />
          )
        }
        return (
          <span className="flex flex-col">
            <span className="tabular">
              {m.umbralOk !== null ? (
                <>
                  en target {op} {formatMetricValue(m.umbralOk, m.unidad)}
                </>
              ) : (
                'sin umbral de target'
              )}
            </span>
            <span className="tabular text-micro text-muted-strong">
              {m.umbralWarning !== null ? (
                <>
                  atención {op} {formatMetricValue(m.umbralWarning, m.unidad)}
                </>
              ) : (
                'sin umbral de atención'
              )}
              {' · '}
              {DIRECCION_LABELS[m.direccion]}
            </span>
          </span>
        )
      },
    },
    {
      key: 'formula',
      header: 'Fórmula',
      width: '200px',
      sortValue: (m) => m.formula,
      render: (m) =>
        m.formula === null ? (
          <SinDato
            que="fórmula"
            onCargar={() => onEditar(m)}
            textoAccion="Publicala"
          />
        ) : (
          <code className="text-micro">{m.formula}</code>
        ),
    },
    {
      key: 'fuente',
      header: 'Fuente',
      width: '130px',
      sortValue: (m) => SOURCE_LABELS[m.source],
      render: (m) => SOURCE_LABELS[m.source],
    },
    {
      key: 'dueno',
      header: 'Dueño',
      width: '150px',
      sortValue: (m) =>
        m.ownerId === null ? null : (nombrePorId.get(m.ownerId) ?? m.ownerId),
      render: (m) => {
        if (m.ownerId === null) {
          return (
            <SinDato
              que="dueño"
              onCargar={() => onEditar(m)}
              textoAccion="Asignalo"
            />
          )
        }
        return nombrePorId.get(m.ownerId) ?? m.ownerId
      },
    },
    {
      key: 'acciones',
      header: 'Editar',
      width: '90px',
      render: (m) => (
        <Button variante="chico" icono="lapiz" onClick={() => onEditar(m)}>
          Editar
          <span className="sr-only"> {m.nombre}</span>
        </Button>
      ),
    },
  ]

  return (
    <Section
      id={id}
      kicker="BLOQUE 2"
      titulo="Agregar un KPI es dar de alta un registro"
      bajada={
        <>
          <b>{metricas.length} métricas</b> definidas.{' '}
          {sinSemaforo > 0 ? (
            <>
              <b>{sinSemaforo}</b> no tienen ningún umbral, así que no pueden
              mostrar semáforo en ninguna pantalla.{' '}
            </>
          ) : null}
          <b>{sinDueno}</b> de {metricas.length} no tienen dueño y <b>{sinFormula}</b>{' '}
          no publican fórmula: sin dueño nadie las carga, y sin fórmula el número
          no se puede recalcular a mano en una reunión.
        </>
      }
      acciones={
        <Button variante="primario" icono="mas" onClick={onAgregar}>
          Agregar KPI
        </Button>
      }
    >
      <DataTable
        columns={columns}
        rows={metricas}
        rowKey={(m) => m.slug}
        rowSeverity={severidadDeMetrica}
        densidadPorSeveridad
        filtroPlaceholder="Nombre, slug o definición"
        emptyMessage="El catálogo está vacío: sin métricas definidas, el panel no tiene nada que mostrar."
        caption={
          <NotaFuente>
            Tabla <code>metrics</code> de D1. Cambiar un umbral acá cambia el
            semáforo de esa métrica en Overview, Revenue y Adquisición; los datos
            ya cargados no se tocan.
          </NotaFuente>
        }
        footer={
          <span className="inline-flex flex-wrap items-center gap-x-3 gap-y-1">
            <span>{metricas.length} métricas en el catálogo</span>
            <span className="inline-flex items-center gap-1">
              <Icon name="critico" size={11} className="text-danger-text" />
              {sinSemaforo} sin umbral
            </span>
            <span className="inline-flex items-center gap-1">
              <Icon name="atencion" size={11} className="text-warning-text" />
              {sinDueno} sin dueño
            </span>
          </span>
        }
      />
    </Section>
  )
}
